/**
 * Fait : l'écran par lequel une personne invitée CRÉE son compte — le nom
 *   qu'elle veut voir affiché, son mot de passe, et la voilà dans l'atelier,
 *   déjà connectée. C'est la seule porte de naissance d'un compte.
 *
 *   Il existe parce que le lien du courriel d'invitation ne menait nulle part :
 *   l'invité arrivait sur l'écran d'entrée, sans compte pour y entrer. Défaut
 *   trouvé par l'étude de terrain du chantier « administration des comptes ».
 *
 * Ne fait pas : il ne demande pas l'adresse électronique — elle est déjà dans
 *   l'invitation, et la ressaisir permettrait de créer un compte pour une
 *   autre adresse que celle qui a été invitée.
 */

import { useState } from 'react';

import { Annonce } from '../gabarits/Annonce.tsx';
import { Champ } from '../gabarits/Champ.tsx';
import { ChampMotDePasse } from '../gabarits/ChampMotDePasse.tsx';
import { Partie } from '../gabarits/Partie.tsx';
import { PromesseDeClannik } from './PromesseDeClannik.tsx';
import { creeSonCompte } from './api.ts';

/** L'aide de l'écran, sortie du JSX pour tenir dans la largeur du code. */
const AIDE =
  'Vous avez été invité(e) dans l’atelier de la famille. Choisissez le nom ' +
  'sous lequel les autres vous verront, et votre mot de passe.';

export function PageInscription({
  jeton,
  surEntree,
}: {
  readonly jeton: string;
  readonly surEntree: () => Promise<void>;
}) {
  const [nomAffiche, setNomAffiche] = useState('');
  const [motDePasse, setMotDePasse] = useState('');
  const [echec, setEchec] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);

  async function cree(evenement: React.FormEvent): Promise<void> {
    evenement.preventDefault();
    setEnCours(true);
    setEchec(null);

    const issue = await creeSonCompte(jeton, nomAffiche.trim(), motDePasse);
    setEnCours(false);

    if (issue.bon) {
      /*
       * Le serveur a posé le cookie : il n'y a pas à se connecter ensuite.
       *
       * L'ORDRE COMPTE. La version d'avant changeait l'adresse PUIS demandait
       * le profil : le temps de l'aller-retour, l'invitée voyait l'écran
       * d'entrée — on lui demandait son mot de passe alors qu'elle venait de
       * le choisir et qu'elle était déjà connectée. Invisible en local,
       * franchement visible sur un lien mobile.
       */
      await surEntree();
      location.hash = '#/';
      return;
    }

    setEchec(issue.erreur.message);
  }

  return (
    <div className="empilement" data-etat="vide" data-ecran="inscription">
      {/* L'avertissement AVANT le formulaire : `D-029` demande qu'il soit lu
          avant de choisir son mot de passe, pas après l'avoir créé. */}
      <PromesseDeClannik />

      <Partie
        etiquette="Bienvenue"
        nature="gens"
        titre="Créer votre compte"
        niveauDeTitre={2}
        aide={AIDE}
        data-role="partie-inscription"
      >
        {echec !== null && (
          <Annonce urgence="urgent" nature="refus">
            {echec}
          </Annonce>
        )}

        <form onSubmit={(evenement) => void cree(evenement)} data-role="inscription">
          <Champ
            libelle="Votre nom"
            aide="Celui que les autres verront — un prénom suffit."
            name="nom-affiche"
            autoComplete="name"
            value={nomAffiche}
            onChange={(evenement) => setNomAffiche(evenement.target.value)}
          />
          <ChampMotDePasse
            libelle="Votre mot de passe"
            name="mot-de-passe-inscription"
            value={motDePasse}
            onChange={setMotDePasse}
          />
          <button
            type="submit"
            data-role="principal"
            disabled={enCours || nomAffiche.trim() === '' || motDePasse === ''}
          >
            {enCours ? 'Création…' : 'Créer mon compte et entrer'}
          </button>
        </form>

        {/* Une porte de sortie : après un refus définitif — invitation
            expirée, déjà utilisée — cet écran était un cul-de-sac. */}
        <p>
          <a href="#/" data-role="vers-la-connexion">
            Revenir à l’écran d’entrée
          </a>
        </p>
      </Partie>
    </div>
  );
}
